import type { TestType } from '@risksmart-app/domain/src/types/consts/test-type';
import type {
  InsertControlTestResultMutation,
  InsertControlTestResultMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTRPC } from '@/utils/trpc';

type InsertControlTestResultTRPCInput = Omit<
  InsertControlTestResultMutationVariables,
  'ControlIds' | 'TestType'
> & {
  ControlIds: string[];
  TestType?: TestType | null;
};

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Ids: string[];
}): InsertControlTestResultMutation => ({
  insertControlTestResult: {
    __typename: 'insertControlTestResultOutput',
    Ids: trpcData.Ids,
  },
});

export const useInsertControlTestResultTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.testResult.insert.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.testResult.testResults.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.testResult.testResultsByControlId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.testResult.latestTestResultsByControlId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.testResult.testResultById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.control.controlById.queryKey(),
        });
        // COI-471 parity: Apollo twin also evicts `risk_score` (test results
        // roll up into control effectiveness / risk scores),
        // `internal_audit_report` and `oversight_assessment`.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.risk.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.internalAuditReport.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.oversight.pathKey(),
          }),
        ]);
      },
    }),
  });

  return {
    insertControlTestResult: async (
      variables: InsertControlTestResultTRPCInput
    ): Promise<InsertControlTestResultMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
